/**
 * A trade's side panel (TRADE-FLOW U2, Pedro 2026-10-25; migration 0122): what it is, its timeline
 * (who / when at every step), its documents and stored emails, and its next steps:
 *
 *   Draft → [Email to client] → With client → [Mark approved] → Approved by client →
 *   [Send for execution] → Sent for execution → [Mark executed] → Executed
 *   With client → [Revise] (a reason; it ends as Revised and a NEW trade, a new number, starts from
 *   its lines) — TF-SIMPLE (Pedro 2026-10-25): no Reject branching, just Mark approved / Revise / Cancel
 *   [Cancel trade] (with a reason) at any point before Executed
 * Every greyed button says why (TF-TIPS).
 *
 * Each step commits at once through its own modal (§3g Pattern B; the panel is not an Edit form).
 * The emails are unsent .eml drafts the user sends from Outlook (the app sends nothing, C-366); the
 * app then asks "Did you send it?" and, on Yes, keeps that same file with the trade (T-F1 / T-F2).
 * Client email: the PDF, to the agreement's people the user ticks. Bank email: the Excel only, to the
 * bank's trade-desk "To" addresses the user ticks, copying its "Cc" ones (T-F4 / T-F5).
 * Steps: `trades.draft` holders (T-F8). No maker-checker on trades yet (Pedro: "not yet, remind me").
 *
 * TRADE-CONS 4e: the step windows take a `StepTarget` (a trade, or a run — RunPanel), so a run's
 * steps read and work exactly as a trade's.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Button, Checkbox, Divider, Drawer, FileInput, Group, Modal, Stack, Text, Textarea, Timeline, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMail, IconPaperclip } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { tradesApi, type TradeContact, type TradeDraft, type TradeEmailOptions, type TradeFileRef } from '../api/trades';
import { dateOf, timeOf, TradeStatusBadge } from './TradesList';

export type Step = 'client_email' | 'approve' | 'revise' | 'bank_email' | 'execute' | 'cancel';

/** A button greyed with its reason in a tooltip (TF-TIPS: a greyed button always says why). */
export function Why({ why, children }: { why: string | null; children: React.ReactElement }) {
  return (
    <Tooltip label={why} disabled={!why} withArrow multiline w={260}>
      <span style={{ display: 'inline-block' }}>{children}</span>
    </Tooltip>
  );
}

export const at = (iso: string | null | undefined) => `${dateOf(iso)} ${timeOf(iso)}`;
export const fail = (title: string, e: unknown) =>
  notifications.show({ color: 'red', title, message: e instanceof Error ? e.message : 'Something went wrong.', autoClose: false });

/** What a step window acts on: one trade, or a run (4e) — or, for a run's bank email / Mark
 *  executed, one bank / one portfolio of it. `noun` words the texts ("trade" / "run"). */
export interface StepTarget {
  key: string;
  serial: string;
  noun: 'trade' | 'run';
  /** The bank email: whose trade desk, and which portfolios it moves (a run's bank). */
  bankNote?: string;
  emailOptions: () => Promise<{ client: TradeEmailOptions['client']; bank: { bank: string | null; to: TradeContact[]; cc: TradeContact[] } }>;
  prepareEmail: (kind: 'client' | 'bank', ids: string[]) => Promise<File>;
  emailSent: (kind: 'client' | 'bank', file: File) => Promise<unknown>;
  approve: (note: string, evidence: File | null) => Promise<unknown>;
  /** Revise: returns the new number, after opening the new one in trade mode. */
  revise: (reason: string) => Promise<string | null>;
  execute: () => Promise<unknown>;
  cancel: (reason: string) => Promise<unknown>;
}

function draftTarget(t: TradeDraft, onOpen: (t: TradeDraft) => void): StepTarget {
  return {
    key: t.id, serial: t.serial ?? '', noun: 'trade',
    emailOptions: () => tradesApi.emailOptions(t.id),
    prepareEmail: (kind, ids) => (kind === 'client' ? tradesApi.prepareClientEmail(t.id, ids) : tradesApi.prepareBankEmail(t.id, ids)),
    emailSent: (kind, file) => (kind === 'client' ? tradesApi.clientEmailSent(t.id, file) : tradesApi.bankEmailSent(t.id, file)),
    approve: (note, evidence) => tradesApi.approve(t.id, note, evidence),
    revise: async (reason) => {
      const r = await tradesApi.reject(t.id, reason, 'revise');
      if (r.new_draft) onOpen(r.new_draft);
      return r.new_draft?.serial ?? null;
    },
    execute: () => tradesApi.execute(t.id),
    cancel: (reason) => tradesApi.cancel(t.id, reason),
  };
}

export function TradePanel({ trade: t, onClose, canAct, override, onSelect, onOpen, onSelectRun }: {
  trade: TradeDraft | null;
  onClose: () => void;
  canAct: boolean;
  override: boolean;
  onSelect: (id: string) => void;
  onOpen: (t: TradeDraft) => void;
  /** 4e: open the panel of the run this draft belongs to. */
  onSelectRun?: (runId: string) => void;
}) {
  const [step, setStep] = useState<Step | null>(null);
  useEffect(() => { setStep(null); }, [t?.id]);
  if (!t) return <Drawer opened={false} onClose={onClose} />;

  const s = t.status ?? 'draft';
  const final = s === 'executed' || s === 'cancelled' || s === 'revised';
  // O8 (TRADE-CONS): a run's draft takes its steps through its run (4e: the run's panel).
  const noPerm = canAct ? (t.run_id ? `Part of run ${t.run_serial}: its steps are taken on the run's panel` : null)
    : 'Needs the Draft trades permission';
  const file = (f: TradeFileRef | null | undefined, label: string) => f ? (
    <Anchor size="xs" onClick={() => tradesApi.openFile(t.id, f).catch((e) => fail('File not opened', e))}>
      <Group gap={4} wrap="nowrap" component="span"><IconPaperclip size={12} />{label}</Group>
    </Anchor>
  ) : null;
  // 4e (smoke 2026-11-01): a run's emails are kept with the run — say where, rather than show nothing.
  const runFile = (f: TradeFileRef | null | undefined, label: string) => file(f, label) ?? (t.run_id ? (
    <Text size="xs" c="dimmed">The email is kept with run{' '}
      {onSelectRun ? <Anchor size="xs" onClick={() => onSelectRun(t.run_id!)}>{t.run_serial}</Anchor> : t.run_serial}</Text>
  ) : null);
  const action = (label: string, key: Step, why: string | null = null, color?: string) => (
    <Why why={noPerm ?? why}>
      <Button size="xs" color={color} variant={color ? 'light' : 'filled'} disabled={!!noPerm || !!why}
        onClick={() => setStep(key)}>{label}</Button>
    </Why>
  );
  const target = draftTarget(t, onOpen);

  return (
    <Drawer opened onClose={onClose} position="right" size={480} title={
      <Group gap="xs"><Text fw={700}>{t.serial}</Text><TradeStatusBadge t={t} /></Group>}>
      <Stack gap="sm">
        <div>
          <Text size="sm" fw={600}>{t.title ?? 'Untitled'}</Text>
          <Text size="xs" c="dimmed">{t.portfolio_label ?? t.portfolio}{t.agreement_label ? ` · ${t.agreement_label}` : ' · not linked to an agreement'}</Text>
          {t.run_id && (
            <Text size="xs" c="dimmed">This portfolio's part of run{' '}
              {onSelectRun ? <Anchor size="xs" onClick={() => onSelectRun(t.run_id!)}>{t.run_serial}</Anchor> : t.run_serial}
              {' '}(a consolidation traded as one): changed and stepped only through the run.</Text>
          )}
          <Text size="xs" c="dimmed">{t.line_count} order{t.line_count === 1 ? '' : 's'}{t.transfer_count ? `, ${t.transfer_count} transfer${t.transfer_count === 1 ? '' : 's'}` : ''} · positions of {t.as_of_date}</Text>
        </div>
        <Group gap="xs">
          <Text size="xs" fw={600}>Documents:</Text>
          {t.documents_at ? (<>
            <Anchor size="xs" onClick={() => tradesApi.openStored(t.id, 'pdf').catch((e) => fail('Document not opened', e))}>PDF</Anchor>
            <Anchor size="xs" onClick={() => tradesApi.openStored(t.id, 'xlsx').catch((e) => fail('Document not opened', e))}>Excel</Anchor>
            <Text size="xs" c="dimmed">made {at(t.documents_at)}</Text>
          </>) : <Text size="xs" c="dimmed">{t.run_id ? `with run ${t.run_serial}'s documents (its row in Trades)` : 'none yet: save the draft in trade mode to make them'}</Text>}
        </Group>

        <Divider label="Next step" labelPosition="left" />
        {final ? <Text size="sm" c="dimmed">None: this trade is {t.status_label}.</Text> : t.run_id ? (
          <Group gap="xs">
            <Button size="xs" variant="default" disabled={!onSelectRun} onClick={() => onSelectRun?.(t.run_id!)}>
              Open run {t.run_serial}'s panel
            </Button>
          </Group>
        ) : (
          <Group gap="xs">
            {s === 'draft' && (<>
              <Why why={canAct ? null : noPerm}><Button size="xs" variant="default" disabled={!canAct} onClick={() => onOpen(t)}>
                Open in trade mode</Button></Why>
              {action('Email to client', 'client_email',
                !t.documents_at ? 'Save the draft in trade mode first: the email attaches its PDF'
                  : !t.agreement_id ? 'Link this portfolio to an agreement first: the email goes to its people' : null)}
            </>)}
            {s === 'with_client' && (<>{action('Mark approved', 'approve')}{action('Revise', 'revise', null, 'orange')}</>)}
            {s === 'approved' && action('Send for execution', 'bank_email')}
            {s === 'sent' && action('Mark executed', 'execute')}
            {action('Cancel trade', 'cancel', null, 'red')}
          </Group>
        )}
        {s !== 'draft' && override && !t.run_id && (
          <Button size="xs" variant="subtle" color="orange" onClick={() => onOpen(t)}>
            Open in trade mode (Admin override, logged)
          </Button>
        )}

        <Divider label="History" labelPosition="left" />
        <Timeline bulletSize={14} lineWidth={2} active={99}>
          <Timeline.Item title="Created"><Text size="xs" c="dimmed">{t.created_by_name ?? '-'} · {at(t.created_at)}</Text>
            {t.revised_from && <Text size="xs">Revision of <Anchor size="xs" onClick={() => onSelect(t.revised_from!.id)}>{t.revised_from.serial}</Anchor></Text>}
            <Text size="xs" c="dimmed">Last saved: {t.updated_by_name ?? '-'} · {at(t.updated_at)}</Text>
          </Timeline.Item>
          {t.client_pending_at && <Timeline.Item title="Emailed to client">
            <Text size="xs" c="dimmed">{t.client_pending_by_name ?? '-'} · {at(t.client_pending_at)}</Text>{runFile(t.client_email, 'The email sent')}
          </Timeline.Item>}
          {t.approved_at && <Timeline.Item title="Approved by client" color="teal">
            <Text size="xs" c="dimmed">Marked by {t.approved_by_name ?? '-'} · {at(t.approved_at)}</Text>
            {t.approved_note && <Text size="xs">“{t.approved_note}”</Text>}{file(t.approval_file, 'Evidence')}
          </Timeline.Item>}
          {t.rejected_at && <Timeline.Item title={s === 'revised' ? 'Revised' : 'Rejected by client'} color="orange">
            <Text size="xs" c="dimmed">{t.rejected_by_name ?? '-'} · {at(t.rejected_at)}</Text>
            {t.cancel_reason && <Text size="xs">Reason: {t.cancel_reason}</Text>}
            {s === 'revised' && t.revised_as && <Text size="xs">Continued as <Anchor size="xs" onClick={() => onSelect(t.revised_as!.id)}>{t.revised_as.serial}</Anchor></Text>}
          </Timeline.Item>}
          {t.sent_at && <Timeline.Item title="Sent for execution">
            <Text size="xs" c="dimmed">{t.sent_by_name ?? '-'} · {at(t.sent_at)}</Text>{runFile(t.bank_email, 'The email sent')}
          </Timeline.Item>}
          {t.executed_at && <Timeline.Item title="Executed" color="green">
            <Text size="xs" c="dimmed">Marked by {t.executed_by_name ?? '-'} · {at(t.executed_at)}</Text>
          </Timeline.Item>}
          {t.cancelled_at && <Timeline.Item title="Cancelled" color="red">
            <Text size="xs" c="dimmed">{t.cancelled_by_name ?? '-'} · {at(t.cancelled_at)}</Text>
            {!t.rejected_at && t.cancel_reason && <Text size="xs">Reason: {t.cancel_reason}</Text>}
          </Timeline.Item>}
        </Timeline>
      </Stack>

      <StepWindow step={step} target={target} onClose={() => setStep(null)} />
    </Drawer>
  );
}

/** The window of a step, for a trade or a run. */
export function StepWindow({ step, target, onClose }: { step: Step | null; target: StepTarget; onClose: () => void }) {
  if (step === 'client_email' || step === 'bank_email') return <EmailModal t={target} kind={step === 'client_email' ? 'client' : 'bank'} onClose={onClose} />;
  if (step === 'approve') return <ApproveModal t={target} onClose={onClose} />;
  if (step === 'revise') return <ReviseModal t={target} onClose={onClose} />;
  if (step === 'execute' || step === 'cancel') return <ReasonModal t={target} kind={step} onClose={onClose} />;
  return null;
}

function useRefresh() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['trades'] });
}

/** Email to client / Send for execution: choose who, prepare the .eml, then "Did you send it?". */
function EmailModal({ t, kind, onClose }: { t: StepTarget; kind: 'client' | 'bank'; onClose: () => void }) {
  const refresh = useRefresh();
  const opts = useQuery({ queryKey: ['trades', 'email-options', t.key], queryFn: t.emailOptions });
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [prepared, setPrepared] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const client = kind === 'client';
  const people = client
    ? (opts.data?.client.recipients ?? []).map((r) => ({ id: r.person_id, name: r.name, email: r.email, note: r.roles }))
    : (opts.data?.bank.to ?? []).map((c) => ({ id: c.id, name: c.name, email: c.email, note: '' }));
  // Default: the KYC contact (client); the only trade-desk address (bank).
  const dflt = client
    ? people.filter((p) => p.id === opts.data?.client.kyc_contact_id && p.email).map((p) => p.id)
    : (people.length === 1 ? [people[0].id] : []);
  const ticks = chosen ?? dflt;

  const prepare = async () => {
    setBusy(true);
    try {
      setPrepared(await t.prepareEmail(kind, ticks));
    } catch (e) { fail('Email not prepared', e); } finally { setBusy(false); }
  };
  const sent = async () => {
    if (!prepared) return;
    setBusy(true);
    try {
      await t.emailSent(kind, prepared);
      await refresh();
      notifications.show({ color: 'green', message: `${t.serial}: ${client ? 'with the client for approval' : t.bankNote ? `${t.bankNote} sent for execution` : 'sent for execution'} · the email is kept with the ${t.noun}` });
      onClose();
    } catch (e) { fail('Not saved', e); } finally { setBusy(false); }
  };

  return (
    <Modal opened onClose={onClose} title={`${client ? 'Email to client' : 'Send for execution'} · ${t.serial}`} size="md">
      {!prepared ? (
        <Stack gap="sm">
          <Text size="sm">
            {client ? `The trade orders (PDF, in the agreement's language) go to the people of ${opts.data?.client.agreement_label ?? 'the agreement'} you tick.`
              : `The trade orders (Excel) go to the trade desk of ${opts.data?.bank.bank ?? 'the bank'}: tick the address(es).`}
            {' '}The app prepares an unsent email for Outlook; nothing is sent from here.
          </Text>
          {!client && t.bankNote && <Text size="xs" c="dimmed">For {t.bankNote}.</Text>}
          {opts.isLoading && <Text size="sm" c="dimmed">Loading…</Text>}
          {!opts.isLoading && !people.length && (
            <Alert color="orange">{client ? 'The agreement has no people to email.'
              : 'This bank has no trade-desk email. An Admin adds one in Admin → Banks: a contact with Trade orders set to To.'}</Alert>
          )}
          {people.map((p) => (
            <Checkbox key={p.id} disabled={!p.email} checked={ticks.includes(p.id)} title={p.email ? undefined : 'No email on file: update the record to email them'}
              label={<>{p.name}{p.note ? <Text span size="xs" c="dimmed"> · {p.note}</Text> : null}</>}
              description={p.email ?? 'No email on file: update the record to email them'}
              onChange={(e) => setChosen(e.currentTarget.checked ? [...ticks, p.id] : ticks.filter((x) => x !== p.id))} />
          ))}
          {!client && !!opts.data?.bank.cc.length && (
            <Text size="xs" c="dimmed">Copied (Cc): {opts.data.bank.cc.map((c) => c.email ?? c.name).join(', ')}</Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Why why={ticks.length ? null : client ? 'Tick who the email goes to first.' : 'Tick the trade-desk address(es) first.'}>
              <Button leftSection={<IconMail size={14} />} onClick={() => void prepare()} loading={busy} disabled={!ticks.length}>Prepare email</Button>
            </Why>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          <Text size="sm">The email <b>{prepared.name}</b> was downloaded: open it, check it and send it from Outlook.</Text>
          <Text size="sm" fw={600}>Did you send it?</Text>
          <Text size="xs" c="dimmed">
            Yes keeps this email with the {t.noun} and moves {client ? `it to With client (it can no longer be changed)`
              : t.bankNote ? `${t.bankNote} to Sent for execution` : 'it to Sent for execution'}.
            Not yet leaves it as it is; you can prepare the email again later.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Not yet</Button>
            <Button onClick={() => void sent()} loading={busy}>Yes, sent</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function ApproveModal({ t, onClose }: { t: StepTarget; onClose: () => void }) {
  const refresh = useRefresh();
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await t.approve(note, evidence);
      await refresh();
      notifications.show({ color: 'green', message: `${t.serial}: approved by the client` });
      onClose();
    } catch (e) { fail('Not saved', e); } finally { setBusy(false); }
  };
  return (
    <Modal opened onClose={onClose} title={`Mark approved by client · ${t.serial}`}>
      <Stack gap="sm">
        <Textarea label="Note (optional)" placeholder="e.g. approved by reply email on…" autosize minRows={2} maxLength={1000}
          value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <FileInput label="Evidence (optional)" placeholder="The client's reply: email or PDF" clearable
          leftSection={<IconPaperclip size={14} />} value={evidence} onChange={setEvidence} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button color="teal" onClick={() => void go()} loading={busy}>Mark approved</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** TF-SIMPLE (Pedro 2026-10-25): Revise, with a reason — this trade ends as Revised and a new trade
 *  (a new number) starts from its orders, opened in trade mode. A run (S4-Q7): the whole run. */
function ReviseModal({ t, onClose }: { t: StepTarget; onClose: () => void }) {
  const refresh = useRefresh();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const serial = await t.revise(reason);
      await refresh();
      onClose();
      if (serial) notifications.show({ color: 'green', message: `${t.serial} revised as ${serial}: opening it in trade mode` });
    } catch (e) { fail('Not saved', e); } finally { setBusy(false); }
  };
  return (
    <Modal opened onClose={onClose} title={`Revise · ${t.serial}`}>
      <Stack gap="sm">
        <Text size="sm">{t.serial} ends as <b>Revised</b>. A new {t.noun}, with a new number, starts from its orders and opens in trade mode.</Text>
        <Textarea label="Reason" placeholder="e.g. the client asked to sell less" required autosize minRows={2} maxLength={1000}
          value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Back</Button>
          <Why why={reason.trim() ? null : 'Give the reason first.'}>
            <Button color="orange" onClick={() => void go()} loading={busy} disabled={!reason.trim()}>Revise</Button>
          </Why>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Mark executed (a confirm) / Cancel trade (a confirm with a reason, T-F6). */
function ReasonModal({ t, kind, onClose }: { t: StepTarget; kind: 'execute' | 'cancel'; onClose: () => void }) {
  const refresh = useRefresh();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const cancel = kind === 'cancel';
  const go = async () => {
    setBusy(true);
    try {
      await (cancel ? t.cancel(reason) : t.execute());
      await refresh();
      notifications.show({ color: 'green', message: `${t.serial}: ${cancel ? 'cancelled' : 'executed'}` });
      onClose();
    } catch (e) { fail('Not saved', e); } finally { setBusy(false); }
  };
  const label = cancel ? `Cancel ${t.noun}` : 'Mark executed';
  return (
    <Modal opened onClose={onClose} title={`${label} · ${t.serial}`}>
      <Stack gap="sm">
        <Text size="sm">{cancel ? `${t.serial} ends as Cancelled${t.noun === 'run' ? ', with every portfolio of it' : ''}. This cannot be undone.`
          : `The bank executed the orders of ${t.serial}. This is final.`}</Text>
        {cancel && <Textarea label="Reason" required autosize minRows={2} maxLength={1000}
          value={reason} onChange={(e) => setReason(e.currentTarget.value)} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Back</Button>
          <Why why={cancel && !reason.trim() ? 'Give the reason first.' : null}>
            <Button color={cancel ? 'red' : 'green'} onClick={() => void go()} loading={busy} disabled={cancel && !reason.trim()}>
              {label}
            </Button>
          </Why>
        </Group>
      </Stack>
    </Modal>
  );
}
